/**
 * 会员详情 — GET /api/admin/users/:id · 通过 ?id= 指定用户
 */
import { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Alert, Button, Descriptions, Spin, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { TOKEN_IDS } from '@uo/shared';

interface UserDetail {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  status: string;
  level_code: string;
  invite_code: string | null;
  referrer_id: number | null;
  parent_id: number | null;
  position: string | null;
  frozen_reason: string | null;
  balances: Record<string, string>;
  create_time: string;
  last_login_at: string | null;
}

export function UserDetailPage() {
  const [search] = useSearchParams();
  const idParam = search.get('id');
  const userId = idParam ? Number.parseInt(idParam, 10) : NaN;
  const valid = Number.isFinite(userId) && userId > 0;

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => api.get<UserDetail>(`/api/admin/users/${userId}`),
    enabled: valid,
  });

  const balanceRows = useMemo(() => {
    if (!data?.balances) return [];
    return TOKEN_IDS.map((asset) => ({
      key: asset,
      asset,
      amount: data.balances[asset] ?? '0',
    }));
  }, [data]);

  if (!valid) {
    return (
      <Alert
        type="info"
        showIcon
        message="请选择会员"
        description="从「会员列表」点击「详情」打开，或在地址栏加上查询参数：/platform/user/detail?id=用户ID"
      />
    );
  }

  if (isFetching && !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (isError || !data) {
    const desc =
      error instanceof ApiError ? error.message : error instanceof Error ? error.message : '无法读取该用户';
    return <Alert type="error" message="加载失败" description={desc} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/platform/users">
          <Button size="small">← 返回会员列表</Button>
        </Link>
      </div>

      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="ID">{data.id}</Descriptions.Item>
        <Descriptions.Item label="用户名">{data.username}</Descriptions.Item>
        <Descriptions.Item label="邮箱">{data.email ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="手机">{data.phone ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="等级">{data.level_code}</Descriptions.Item>
        <Descriptions.Item label="状态">
          <Tag color={data.status === 'active' ? 'green' : 'red'}>{data.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="邀请码">{data.invite_code ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="推荐人 ID">{data.referrer_id ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="注册时间">{data.create_time}</Descriptions.Item>
        <Descriptions.Item label="最近登录">{data.last_login_at ?? '—'}</Descriptions.Item>
        {data.frozen_reason && (
          <Descriptions.Item label="冻结原因" span={2}>
            {data.frozen_reason}
          </Descriptions.Item>
        )}
      </Descriptions>

      <h3 style={{ marginBottom: 8 }}>资产余额</h3>
      <Table
        size="small"
        bordered
        rowKey="key"
        pagination={false}
        dataSource={balanceRows}
        columns={[
          { title: '资产', dataIndex: 'asset', render: (v: string) => v.toUpperCase() },
          { title: '数量', dataIndex: 'amount', render: (v: string) => <code>{v}</code> },
        ]}
      />
    </div>
  );
}
